// ──────────────────────────────────────────────────────────────────────────
// TRE LIVELLI, NON DUE
//
// Un circolo tiene insieme due cose diverse, e vanno separate:
//
//   il luogo      nome, indirizzo, campi, superfici. Sono di chi il circolo
//                 lo possiede, e uguali per chiunque ci insegni.
//
//   le condizioni quanto fa pagare l'allievo, cosa accetta come pagamento,
//                 quanto paga il campo. Non sono del circolo: sono del
//                 maestro, in quel circolo. Stanno nella sua parte privata
//                 (`coaches/{id}/private/settings`, che legge solo lui),
//                 divise per circolo, accanto a `billingData`.
//
// In lettura vince il maestro; dove non ha detto niente si eredita dal
// circolo. Un circolo che usa l'app può così dare un listino comune, e chi
// ha condizioni sue se le tiene senza chiedere permesso a nessuno.
// ──────────────────────────────────────────────────────────────────────────
import { COURT_PAID_BY_COACH, PAID_TO_COACH } from "./billing.utils"
import { lessonTypeHasCourt, lessonTypeNamesCanWait, withLessonTypeDefaults } from "./func.utils"

export const SCHOOL_SETTINGS_FIELDS = [
	"lessonPrices",
	"availablePaymentMethods",
	"availableLessonTypes",
	"courtsFee",
	"courtPaidBy",
	"playersPayTo",
]

const ownSettings = (coach, schoolID) => coach?.schoolSettings?.[schoolID] ?? {}

export const getSchoolSettings = (coach, school, schoolID) => {
	const own = ownSettings(coach, schoolID)

	return {
		lessonPrices: own.lessonPrices ?? school?.lessonPrices ?? {},
		availablePaymentMethods:
			own.availablePaymentMethods ?? school?.availablePaymentMethods ?? [],
		availableLessonTypes: own.availableLessonTypes ?? school?.availableLessonTypes ?? {},
		// `coach.courtsFee` è la vecchia tariffa unica, da prima che le
		// tariffe fossero divise per circolo: resta come ultima spiaggia
		// finché non è stata spostata al posto giusto
		courtsFee: own.courtsFee ?? school?.courtsFee ?? coach?.courtsFee ?? null,
		// Di chi è il campo delle lezioni private, quando non lo si dice
		// lezione per lezione. Chi non ha mai scelto paga il campo di tasca
		// sua: è così che ha sempre funzionato.
		courtPaidBy: own.courtPaidBy ?? school?.courtPaidBy ?? COURT_PAID_BY_COACH,
		// A chi pagano di solito gli allievi delle private: al maestro, o al
		// circolo che poi gli gira il dovuto. Chi non ha mai scelto viene
		// pagato lui: è così che ha sempre funzionato.
		playersPayTo: own.playersPayTo ?? school?.playersPayTo ?? PAID_TO_COACH,
		// Le tariffe al circolo non si ereditano da nessuno: quello che il
		// circolo paga a te lo sai solo tu. Vivono su un ramo loro del
		// documento, ma si decidono insieme al listino dei privati, quindi
		// si leggono da qui.
		billingRates: coach?.billingData?.[schoolID] ?? {},
		// Le tipologie che il circolo si è scritto: non sono condizioni del
		// maestro — un maestro non ha tipologie esclusive, fa quello che nel
		// circolo si fa — ma si leggono da qui perché è qui che si decide
		// quali lezioni si fanno e quanto costano.
		schoolLessonTypes: school?.lessonTypes ?? {},
	}
}

// Se il valore mostrato è una scelta del maestro o un'eredità del circolo:
// serve all'interfaccia per dire da dove arriva un numero.
export const isOwnSetting = (coach, schoolID, field) =>
	ownSettings(coach, schoolID)[field] !== undefined

// ──────────────────────────────────────────────────────────────────────────
// RUOLI E POTERI NEL CIRCOLO
//
// Il proprietario può tutto, ed è l'unico che decide cosa possono gli altri.
// A un collega dà le lezioni dei colleghi — di tutti o di alcuni, da vedere
// o da modificare — e due poteri sul circolo: far entrare colleghi e
// cambiare campi, listino e dati. I ruoli (collega, osservatore, gestore)
// sono solo scorciatoie per le combinazioni più comuni.
//
// Sul documento del maestro:
//   managedSchools                     modifica le lezioni di tutti
//   managedCoachesBySchool[schoolID]   modifica le lezioni di questi
//   seenCoachesBySchool[schoolID]      vede le lezioni di questi
//   schoolPowers[schoolID]             { invite, editSchool, seesAll }
//
// Far entrare colleghi non si sceglie a parte: lo fanno il proprietario e i
// gestori, chi modifica le lezioni di tutti. Un `invite` scritto quando lo si
// sceglieva uno per uno non conta più.
//
// Chi non ha una voce in `schoolPowers` è di prima che i poteri si
// scegliessero uno per uno: il gestore di allora cambia il circolo. Il server
// dice la stessa cosa (`schoolPowersOf` in functions/index.js).
// ──────────────────────────────────────────────────────────────────────────

// `managedSchools` del maestro collegato arriva in id; quello dei colleghi,
// letto così com'è, in reference
const includesSchool = (list, schoolID) =>
	Boolean(schoolID) && (list ?? []).some(item => (item?.id ?? item) === schoolID)

export const ownsSchool = (coach, school) =>
	Boolean(coach?.coachID && school?.ownerCoachID === coach.coachID)

export const schoolPowersOf = (coach, school, schoolID) => {
	if (!coach || !schoolID) return { invite: false, editSchool: false }
	if (ownsSchool(coach, school)) return { invite: true, editSchool: true }

	const managesAll = includesSchool(coach.managedSchools, schoolID)
	const explicit = coach.schoolPowers?.[schoolID]
	return {
		invite: managesAll,
		editSchool: explicit ? explicit.editSchool === true : managesAll,
	}
}

// Il luogo lo modifica chi lo possiede — di solito chi l'ha creato — oppure
// chi ha avuto dal proprietario il permesso. Insegnare qui non basta.
export const canEditSchool = (coach, school, schoolID) =>
	schoolPowersOf(coach, school, schoolID).editSchool

export const canInviteTo = (coach, school, schoolID) =>
	schoolPowersOf(coach, school, schoolID).invite

// Cosa può fare un collega sulle lezioni degli altri e sul circolo, nella
// forma che legge e scrive la scheda Maestri (e la callable
// `setColleagueAccess`):
//   allLessons  "none" | "view" | "edit"
//   coaches     { coachID: "view" | "edit" }
//   invite, editSchool
export const accessOf = (coach, school, schoolID) => {
	const managesAll = includesSchool(coach?.managedSchools, schoolID)
	const seesAll = coach?.schoolPowers?.[schoolID]?.seesAll === true
	const coaches = {}
	;(coach?.seenCoachesBySchool?.[schoolID] ?? []).forEach(id => {
		coaches[id] = "view"
	})
	;(coach?.managedCoachesBySchool?.[schoolID] ?? []).forEach(id => {
		coaches[id] = "edit"
	})

	return {
		allLessons: managesAll ? "edit" : seesAll ? "view" : "none",
		coaches: managesAll ? {} : coaches,
		...schoolPowersOf(coach, school, schoolID),
	}
}

// Le scorciatoie: lezioni di tutti e poteri sul circolo. Le lezioni di
// alcuni colleghi, uno per uno, si tengono qualunque ruolo si scelga.
export const ROLE_PRESETS = {
	colleague: { allLessons: "none", invite: false, editSchool: false },
	viewer: { allLessons: "view", invite: false, editSchool: false },
	manager: { allLessons: "edit", invite: true, editSchool: true },
}

export const ROLE_LABELS = {
	owner: "Proprietario",
	colleague: "Collega",
	viewer: "Osservatore",
	manager: "Gestore",
	custom: "Personalizzato",
}

// Cosa dice il codice a chi lo digita, prima di entrare: con che ruolo, e se
// ci insegnerà. I codici di prima dicevano solo se si gestiva.
export const inviteEntryText = (preview, invitedBy) => {
	const role = preview?.role ?? (preview?.canManageSchool ? "manager" : "colleague")
	const teaches = preview?.teaches !== false

	if (role === "manager")
		return teaches
			? "Ci entri come gestore: potrai cambiare campi e dati del circolo."
			: "Ci entri come gestore, senza insegnare: guardi e sistemi le lezioni dei maestri, cambi campi e dati del circolo."
	if (role === "viewer")
		return teaches
			? "Ci entri come osservatore: segni le tue lezioni e vedi quelle di tutti i maestri."
			: "Ci entri come osservatore, senza insegnare: vedi le lezioni di tutti i maestri, senza toccarle."
	return teaches
		? `Ci entri come maestro: segni le tue lezioni, il circolo lo gestisce ${invitedBy}.`
		: "Ci entri senza insegnare: non hai una giornata tua, e quali lezioni guardi lo decide il proprietario del circolo."
}

export const roleOf = (coach, school, schoolID) => {
	if (ownsSchool(coach, school)) return "owner"

	const access = accessOf(coach, school, schoolID)
	const matches = Object.entries(ROLE_PRESETS).find(([, preset]) =>
		Object.entries(preset).every(([key, value]) => access[key] === value),
	)
	return matches?.[0] ?? "custom"
}

// ──────────────────────────────────────────────────────────────────────────
// CHI DIRIGE IL CIRCOLO
//
// Chi crea un circolo ne è il proprietario nell'app, ma non sempre lo
// dirige: spesso è un maestro che l'app l'ha portata lui. `runByOwner` sul
// circolo dice quale dei due: vero se il proprietario lo dirige, falso se ci
// insegna e basta, assente se non l'ha mai detto (chi l'ha creato prima che
// lo si chiedesse). Vedi "Chi dirige il circolo" in docs/circoli.md.
//
// I Primi passi del proprietario ne fanno un passo:
//   "ask"       non l'ha mai detto: glielo si chiede
//   "propose"   ci insegna: proponga l'app a chi lo dirige
//   "proposed"  la direzione è entrata, da gestore: il passo è fatto
// e niente (null) a chi lo dirige e a chi non ne è il proprietario.
// `school.coaches` è il dizionario dei maestri, già arrivati.
// ──────────────────────────────────────────────────────────────────────────
export const ownershipStepOf = (coach, school, schoolID) => {
	if (!ownsSchool(coach, school) || school.runByOwner === true) return null
	if (school.runByOwner !== false) return "ask"

	const managerArrived = Object.values(school.coaches ?? {}).some(
		other =>
			other?.coachID !== coach.coachID && roleOf(other, school, schoolID) === "manager",
	)
	return managerArrived ? "proposed" : "propose"
}

// ──────────────────────────────────────────────────────────────────────────
// UN TIPO DI LEZIONE NON SI CANCELLA
//
// Gli ID dei tipi finiscono dentro ogni lezione salvata: cancellarne uno vuol
// dire che una lezione di due anni fa non sa più come si chiama. Per smettere
// di proporre un tipo si marca `deprecated` sul catalogo, e il documento resta
// dov'è.
//
// Deprecato però non vuol dire sparito: chi lo usa già ce l'ha nella sua lista
// e se lo tiene, prezzo compreso, finché non lo toglie lui. Non si propone a
// chi non ce l'ha — e a chi non ha mai scelto niente, che si vede proporre
// quello che lo sport prevede.
// ──────────────────────────────────────────────────────────────────────────

// Quelli che lo sport propone a chi non ha ancora scelto.
export const getProposedLessonTypes = (sport, getLessonType) =>
	(sport?.lessonTypes ?? []).filter(lessonType => !getLessonType?.(lessonType)?.deprecated)

// ──────────────────────────────────────────────────────────────────────────
// L'IDENTIFICATIVO DI UNA TIPOLOGIA DEL CIRCOLO
//
// Comincia per `club_` e non può collidere col catalogo globale, che quel
// prefisso non lo usa: è il server a pretenderlo, perché questo ID finisce
// dentro ogni lezione salvata con questa tipologia, e due tipologie con lo
// stesso ID vorrebbero dire una lezione che cambia nome da sola.
//
// Dopo lo slug del nome c'è una coda casuale, e serve: due tipologie chiamate
// uguali sono legittime, e soprattutto una tipologia cancellata e una nuova
// col nome di prima non devono essere la stessa cosa, o le lezioni della
// prima si ritroverebbero il nome della seconda.
//
// Lo slug è solo per chi un giorno leggerà `own_live_ball_k3x9` dentro una
// lezione e vorrà capire cosa sia: il nome vero sta sul documento, e
// cambiarlo non cambia l'ID.
// ──────────────────────────────────────────────────────────────────────────
export const newSchoolLessonTypeID = lessonTypeName => {
	const slug = (lessonTypeName ?? "")
		.toLowerCase()
		.normalize("NFD")
		// via gli accenti: l'ID accetta solo lettere senza fronzoli
		.replace(/[\u0300-\u036f]/g, "")
		.replace(/[^a-z0-9]+/g, "_")
		.replace(/^_+|_+$/g, "")
		.slice(0, 24)

	const tail = Math.random().toString(36).slice(2, 6)

	return `club_${slug ? `${slug}_` : ""}${tail}`
}

// ──────────────────────────────────────────────────────────────────────────
// QUELLO CHE UN CIRCOLO SI SCRIVE, E QUELLO CHE CORREGGE
//
// Su `schools/{id}.lessonTypes` finiscono due cose diverse, e si riconoscono
// dal nome:
//
//   con un nome    è una tipologia sua — il live ball, la cosa che lì dentro
//                  chiamano solo loro in quel modo. Nasce lì, si legge tutta
//                  da lì, e dall'app si cambia tutta.
//
//   senza un nome  non è una tipologia, è una correzione a una del catalogo:
//                  quanti allievi ci stanno *qui*, se i nomi si segnano
//                  *qui*. Come si chiama e cos'è continua a dirlo il
//                  catalogo, che è di tutti e non lo riscrive un circolo.
//
// La differenza conta anche in lettura. Una tipologia sua copre la globale
// per intero, ed è voluto: è così che una che ha smesso di essere standard
// continua a funzionare dove la usavano, senza ereditarne il `deprecated`.
// Una correzione invece ci si appoggia sopra, e quello che non dice resta
// quello del catalogo — compresa una descrizione che il catalogo si scriverà
// domani.
// ──────────────────────────────────────────────────────────────────────────

// Se quella del circolo è una tipologia sua o solo una correzione.
export const isOwnLessonType = schoolLessonType => Boolean(schoolLessonType?.lessonTypeName)

// Un tipo di lezione come è qui dentro.
export const resolveLessonType = (catalogType, schoolLessonType) => {
	if (!schoolLessonType) return catalogType
	if (isOwnLessonType(schoolLessonType)) return schoolLessonType

	// SAT e CAD i giocatori li hanno sempre: una correzione che li spegneva,
	// scritta quando si poteva, non conta più.
	if (lessonTypeNamesCanWait(catalogType)) {
		const { needsPlayers, ...rest } = schoolLessonType
		return { ...catalogType, ...rest }
	}

	return { ...catalogType, ...schoolLessonType }
}

// Il catalogo come lo vede questo circolo: da fuori è un catalogo solo, e
// chi legge un tipo non deve sapere da quale dei due arriva.
export const resolveLessonTypes = (catalogTypes, schoolLessonTypes) => ({
	...catalogTypes,
	...Object.fromEntries(
		Object.entries(schoolLessonTypes ?? {}).map(([lessonTypeID, schoolLessonType]) => [
			lessonTypeID,
			withLessonTypeDefaults(
				lessonTypeID,
				resolveLessonType(catalogTypes?.[lessonTypeID], schoolLessonType),
			),
		]),
	),
})

// Tutti quelli che si possono mettere nella propria lista, e sono tre cose:
//
//   quelli che lo sport propone, meno i deprecati
//
//   quelli che un campo non lo occupano — il centro estivo, le racchette in
//   classe — che a uno sport non appartengono e nella sua lista non stanno:
//   si potevano scegliere dalla riga in cima alla nuova lezione ma non dal
//   listino, quindi non si potevano né prezzare né togliere
//
//   le tipologie del circolo, che sono sue e non di uno sport. Quelle non
//   sono mai deprecate — se le è scritte lui — quindi toglierle dalla lista
//   non le fa sparire, al contrario di un SAT Pro.
export const getOfferableLessonTypes = (sport, lessonTypes, schoolLessonTypes) => {
	// la bozza del listino può avere una tipologia appena scritta, che nel
	// catalogo non c'è ancora
	const getLessonType = lessonTypeID =>
		resolveLessonType(lessonTypes?.[lessonTypeID], schoolLessonTypes?.[lessonTypeID])

	const courtless = Object.keys(lessonTypes ?? {}).filter(
		lessonTypeID =>
			!lessonTypes[lessonTypeID]?.deprecated &&
			!lessonTypeHasCourt(lessonTypes[lessonTypeID]),
	)

	// solo le tipologie sue: una correzione non è un tipo in più da offrire,
	// è una riga scritta sopra a uno che il catalogo offre già
	const ownTypes = Object.keys(schoolLessonTypes ?? {}).filter(lessonTypeID =>
		isOwnLessonType(schoolLessonTypes[lessonTypeID]),
	)

	return [...new Set([...getProposedLessonTypes(sport, getLessonType), ...courtless, ...ownTypes])]
}

// I tipi di lezione che si fanno qui. Finché nessuno ha scelto, sono quelli
// che lo sport propone: così un circolo che non se n'è mai occupato continua
// a funzionare come prima.
export const getSportLessonTypes = (settings, sportID, sport, getLessonType) =>
	settings?.availableLessonTypes?.[sportID] ?? getProposedLessonTypes(sport, getLessonType)

// ──────────────────────────────────────────────────────────────────────────
// PRINCIPALE E ARCHIVIATI
//
// Come il maestro mette in ordine i suoi circoli, nella sua parte privata:
// `schoolPlacement[schoolID]` è "primary" o "archived", e un circolo che non
// c'è è semplicemente in uso.
//
//   principale  quello con cui si apre l'app, e il primo nell'elenco
//   archiviato  fuori dal selettore e dalle lezioni degli altri circoli,
//               ma ci si resta: lezioni, allievi e conti di allora si
//               rileggono quando si vuole
//
// Vale solo per i circoli in cui si è: una scelta rimasta su uno da cui si
// è usciti non conta.
// ──────────────────────────────────────────────────────────────────────────
export const PRIMARY_SCHOOL = "primary"
export const ARCHIVED_SCHOOL = "archived"

export const placementOf = (coach, schoolID) => coach?.schoolPlacement?.[schoolID] ?? null

export const isPrimarySchool = (coach, schoolID) => placementOf(coach, schoolID) === PRIMARY_SCHOOL

export const isArchivedSchool = (coach, schoolID) =>
	placementOf(coach, schoolID) === ARCHIVED_SCHOOL

// I circoli in uso, il principale per primo e gli altri nell'ordine in cui
// ci si è entrati. `schoolIDs` sono quelli che si conoscono: si guardano
// solo quelli.
export const activeSchoolIDs = (coach, schoolIDs) => {
	const active = schoolIDs.filter(id => !isArchivedSchool(coach, id))
	return [
		...active.filter(id => isPrimarySchool(coach, id)),
		...active.filter(id => !isPrimarySchool(coach, id)),
	]
}

export const archivedSchoolIDs = (coach, schoolIDs) =>
	schoolIDs.filter(id => isArchivedSchool(coach, id))

// Dove si apre l'app: il principale, se no il primo circolo in uso. Se per
// qualche motivo fossero tutti archiviati, il primo e basta — meglio uno
// storico che una schermata vuota.
export const initialSchoolID = (coach, schoolIDs) =>
	activeSchoolIDs(coach, schoolIDs)[0] ?? schoolIDs[0] ?? null

// Quelli tra cui si passa col selettore: i circoli in uso, e quello
// archiviato che si sta guardando, finché lo si guarda — sparirgli sotto il
// dito vorrebbe dire non sapere più dove si è.
export const switchableSchoolIDs = (coach, schoolIDs, selectedSchoolID) => {
	const active = activeSchoolIDs(coach, schoolIDs)
	return selectedSchoolID &&
		schoolIDs.includes(selectedSchoolID) &&
		!active.includes(selectedSchoolID)
		? [...active, selectedSchoolID]
		: active
}
